/**
 * @file LinkSettingsPage.tsx
 * @brief Super Admin → Links & company info (W19, BACKEND B23, ADR-057)
 *
 * @details
 * DES 24-10-2026: "change in one place, changes everywhere". Store and
 * download links, site pages, contact, company details, social links, the
 * oldest app version allowed and what users are shown, in seven groups.
 * Each value is what every app, email and the website shows now; edit in
 * place, a dot marks a changed row, and one bar at the bottom lists
 * old → new before Save (the Plans & seats bar). Empty = hidden everywhere;
 * "Use default" deletes the saved row.
 *
 * "Still to fill" names the empty values that matter at launch (store
 * links, registered address); clicking one jumps to its field. Nothing is
 * filled in for DES.
 *
 * W20 (25-10-2026): the seven groups are tabs (Downloads | Sites | Contact |
 * Company | Social | App versions | Shown to users), same tab row as Billing.
 * The open tab is the URL hash (#downloads). Typed values, the unsaved count
 * per tab and the one review bar all read the same draft state, so switching
 * tabs loses nothing. A dot on a tab = a "Still to fill" value lives there.
 *
 * Related: ./linkSettings.ts (form model), ./api.ts, BACKEND
 *   src/features/appConfig/linkSettings.mjs
 *
 * @author DES (prepared with Claude)
 * @date 24-10-2026
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Building2,
  Download,
  Eye,
  Globe,
  Mail,
  Share2,
  Smartphone,
  type LucideIcon,
} from "lucide-react";
import { Input } from "@/components/ui/Input";
import { SkeletonFields } from "@/components/ui/Skeleton";
import { Switch } from "@/components/ui/Switch";
import { TooltipProvider } from "@/components/ui/Tooltip";
import {
  GridBand,
  GridBlock,
  GridCell,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { ChangedDot } from "@/features/billing/components/PlanSettingRow";
import { PlanChangesBar } from "@/features/billing/components/PlanChangesBar";
import { SectionTabRow, type SectionTabItem } from "@/features/billing/components/SectionTabRow";
import { ApiClientError } from "@/shared/api/client";
import { cn } from "@/shared/theme/cn";
import { useLinkSettings, useUpdateLinkSettings } from "./api";
import {
  CHOICE_WORDS,
  SECTION_HINTS,
  SECTION_LABELS,
  changedLinkSettings,
  invalidFromServer,
  linkSettingsToForm,
  readableLink,
  resolveLinkTab,
  sortSocialSettings,
  sortTabSections,
  unsavedByTab,
  validateLinkValue,
  type LinkFormValues,
  type LinkSection,
  type LinkSetting,
} from "./linkSettings";

/** Empty values worth filling before launch (B23 "For DES"). */
const TO_FILL = ["APP_STORE_URL", "GOOGLE_PLAY_URL", "COMPANY_ADDRESS"];

/** One line under a field, where the value alone doesn't say enough. */
const FIELD_HINTS: Record<string, string> = {
  APP_STORE_URL: "Empty until the iPhone app is live. The used-up email and the website show it once saved.",
  GOOGLE_PLAY_URL: "Empty until the Android app is live.",
  SUPPORT_EMAIL: "Every email and invoice uses it. The website shows support@verblizr.co.uk.",
  COMPANY_ADDRESS: "The full registered office. Saving it turns on the legal line in emails and invoices.",
  MIN_APP_VERSION_MAC: "The Mac app shows “Please update” below this. The Mac app live today is 0.1.0.",
  MIN_APP_VERSION_IOS: "The phone shows “Update required” below this.",
  MIN_APP_VERSION_ANDROID: "The phone shows “Update required” below this.",
  MAC_ARM64_DMG_URL: "The website's direct download for Apple silicon.",
  MAC_X64_DMG_URL: "The website's direct download for Intel Macs.",
  ZAPIER_APP_URL: "Settings → Integrations → Connect Zapier opens this. The private invite link until launch, then the public app page. Empty = no Connect button.",
  MAKE_APP_URL: "Settings → Integrations → Connect Make opens this. The private invite link until Make publishes the app, then the public app page. Empty = no Connect button.",
  WHATSAPP_URL: "https://wa.me/447… (number) or a WhatsApp channel link.",
  REALTIME_VISIBLE: "Off = Realtime interpretation is hidden in every app.",
};

/** Icon per tab. */
const TAB_ICONS: Record<string, LucideIcon> = {
  downloads: Download,
  sites: Globe,
  contact: Mail,
  company: Building2,
  social: Share2,
  app: Smartphone,
  features: Eye,
};

const fieldId = (key: string) => `link-${key}`;

function LinkRow({
  s,
  value,
  onChange,
  error,
}: {
  s: LinkSetting;
  value: string | null;
  onChange: (v: string | null) => void;
  error: string | null;
}) {
  const shown = value === null ? s.default : value;
  const changed = value === null ? s.saved : value.trim() !== (s.value ?? "").trim();
  const hint = FIELD_HINTS[s.key];
  const resetting = value === null;
  const footer = resetting
    ? `Back to the default on Save: ${readableLink(s, s.default)}`
    : s.saved
      ? `Saved · default ${readableLink(s, s.default)}`
      : "Default";

  return (
    <div className="flex items-start gap-2 py-2.5">
      <span className="mt-2">
        <ChangedDot on={changed} />
      </span>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center justify-between gap-3">
          <label
            htmlFor={fieldId(s.key)}
            title={s.key}
            className={cn("text-sm", error ? "text-destructive" : "text-muted-foreground")}
          >
            {error ? `${s.label}: ${error}` : s.label}
          </label>
          {s.kind === "bool" ? (
            <Switch
              id={fieldId(s.key)}
              checked={shown === "true"}
              onCheckedChange={(on) => onChange(on ? "true" : "false")}
            />
          ) : null}
        </div>

        {s.kind === "choice" ? (
          <div
            id={fieldId(s.key)}
            role="radiogroup"
            aria-label={s.label}
            className="inline-flex flex-wrap rounded-full bg-black/[0.04] p-1 dark:bg-white/[0.06]"
          >
            {(s.options ?? []).map((o) => (
              <button
                key={o}
                type="button"
                role="radio"
                aria-checked={shown === o}
                onClick={() => onChange(o)}
                className={cn(
                  "rounded-full px-3.5 py-1 text-sm font-medium transition-colors",
                  shown === o ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {CHOICE_WORDS[o] ?? o}
              </button>
            ))}
          </div>
        ) : s.kind === "bool" ? null : (
          <Input
            id={fieldId(s.key)}
            value={shown}
            inputMode={s.kind === "email" ? "email" : s.kind === "phone" ? "tel" : s.kind === "version" ? "decimal" : "url"}
            type={s.kind === "email" ? "email" : s.kind === "url" ? "url" : "text"}
            autoComplete="off"
            spellCheck={false}
            placeholder={s.kind === "version" ? "Empty = no forced update" : "Empty = hidden"}
            aria-invalid={!!error}
            onChange={(e) => onChange(e.target.value)}
          />
        )}

        <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-xs text-muted-foreground">
          <span className="min-w-0 break-all">{hint ? `${hint} ` : ""}</span>
          <span className="flex shrink-0 items-baseline gap-2">
            <span>{footer}</span>
            {s.saved && !resetting ? (
              <button type="button" onClick={() => onChange(null)} className="font-medium text-foreground/80 hover:text-foreground">
                Use default
              </button>
            ) : null}
          </span>
        </div>
      </div>
    </div>
  );
}

export function LinkSettingsPage() {
  const query = useLinkSettings();
  const update = useUpdateLinkSettings();
  const sections: LinkSection[] = useMemo(
    () => (query.data?.status === "ok" ? query.data.data.sections : []),
    [query.data],
  );
  const tabSections = useMemo(
    () =>
      sortTabSections(sections).map((sec) =>
        sec.id === "social" ? { ...sec, settings: sortSocialSettings(sec.settings) } : sec,
      ),
    [sections],
  );
  const location = useLocation();
  const navigate = useNavigate();
  const activeId = resolveLinkTab(location.hash, tabSections.map((s) => s.id));
  const activeSection = tabSections.find((s) => s.id === activeId);
  /** Field to focus once its tab has rendered ("Still to fill" click). */
  const focusKey = useRef<string | null>(null);
  useEffect(() => {
    const key = focusKey.current;
    if (!key) return;
    focusKey.current = null;
    document.getElementById(fieldId(key))?.focus();
  }, [activeId]);
  const baseline = useMemo(() => linkSettingsToForm(sections), [sections]);
  const [values, setValues] = useState<LinkFormValues>(baseline);
  useEffect(() => setValues(baseline), [baseline]);
  const [serverInvalid, setServerInvalid] = useState<Record<string, string>>({});
  const [reviewOpen, setReviewOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!saved) return;
    const t = window.setTimeout(() => setSaved(false), 4000);
    return () => window.clearTimeout(t);
  }, [saved]);

  const changes = useMemo(() => changedLinkSettings(sections, values), [sections, values]);
  const byKey = useMemo(() => new Map(sections.flatMap((s) => s.settings).map((s) => [s.key, s])), [sections]);
  const toFill = TO_FILL.map((k) => byKey.get(k)).filter((s): s is LinkSetting => !!s && !(s.value ?? "").trim());

  const sectionOf = useMemo(
    () => new Map(sections.flatMap((sec) => sec.settings.map((x) => [x.key, sec.id] as const))),
    [sections],
  );
  const unsaved = useMemo(
    () => unsavedByTab(sections, changes, Object.keys(serverInvalid)),
    [sections, changes, serverInvalid],
  );
  const fillTabs = useMemo(
    () => new Set(toFill.map((s) => sectionOf.get(s.key))),
    [toFill, sectionOf],
  );
  const tabs: SectionTabItem[] = tabSections.map((sec) => ({
    id: sec.id,
    label: SECTION_LABELS[sec.id] ?? sec.label,
    icon: TAB_ICONS[sec.id] ?? Globe,
    href: `#${sec.id}`,
    count: unsaved[sec.id],
    dot: fillTabs.has(sec.id),
  }));

  const jumpTo = (key: string) => {
    const id = sectionOf.get(key);
    if (!id) return;
    if (id === activeId) {
      document.getElementById(fieldId(key))?.focus();
      return;
    }
    focusKey.current = key;
    navigate(`#${id}`);
  };

  const set = (key: string, v: string | null) => {
    setServerInvalid((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setValues((prev) => ({ ...prev, [key]: v }));
  };

  const onSave = () => {
    if (changes.invalid.length || changes.lines.length === 0) return;
    setServerInvalid({});
    update.mutate(changes.settings, {
      onSuccess: () => setSaved(true),
      onError: (err) => {
        if (err instanceof ApiClientError) setServerInvalid(invalidFromServer(err.raw));
      },
    });
  };

  const errorFor = (s: LinkSetting): string | null => {
    if (serverInvalid[s.key]) return serverInvalid[s.key];
    const v = values[s.key];
    if (v === null || v === undefined) return null;
    if (v.trim() === (s.value ?? "").trim()) return null;
    return validateLinkValue(s, v);
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className={gridPageBleedClass("pb-6")}>
        <GridBlock>
          <GridBand className="py-3">
            <h2 className="text-xl font-semibold tracking-tight">Links &amp; company info</h2>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              One place for every link, email and company line the apps, emails, invoices and the
              website show. Change it here and it changes everywhere within a few minutes. Empty =
              hidden everywhere. Nothing is saved until you review and save.
            </p>
          </GridBand>

          {query.isLoading ? (
            <GridCell>
              <SkeletonFields fields={6} />
            </GridCell>
          ) : query.isError ? (
            <GridCell>
              <p className="text-sm text-destructive">
                Couldn't load Links &amp; company info.{query.error ? ` ${query.error.message}` : ""}
              </p>
            </GridCell>
          ) : query.data?.status === "missing" ? (
            <GridCell>
              <p className="text-sm text-muted-foreground">
                The server doesn't have Links &amp; company info yet (backend B23 not deployed). The apps
                keep today's links until it is.
              </p>
            </GridCell>
          ) : (
            <>
              {toFill.length ? (
                <GridBand className="flex flex-wrap items-baseline gap-x-2 gap-y-1 bg-muted/25 py-2.5 text-sm">
                  <span className="font-medium">Still to fill:</span>
                  {toFill.map((s, i) => (
                    <span key={s.key}>
                      <button
                        type="button"
                        className="text-brand hover:underline"
                        onClick={() => jumpTo(s.key)}
                      >
                        {s.label.replace(/ \(.*\)$/, "")}
                      </button>
                      {i < toFill.length - 1 ? <span className="text-muted-foreground">,</span> : null}
                    </span>
                  ))}
                </GridBand>
              ) : null}

              <SectionTabRow
                tabs={tabs}
                activeId={activeId}
                ariaLabel="Links & company info groups"
                gridClassName=""
                scroll
              />

              {activeSection ? (
                <GridCell
                  key={activeSection.id}
                  role="tabpanel"
                  aria-label={SECTION_LABELS[activeSection.id] ?? activeSection.label}
                  className="flex flex-col gap-1 border-r-0"
                >
                  <div className="pb-1">
                    <p className="text-base font-semibold">
                      {SECTION_LABELS[activeSection.id] ?? activeSection.label}
                    </p>
                    {SECTION_HINTS[activeSection.id] ? (
                      <p className="mt-0.5 text-xs text-muted-foreground">{SECTION_HINTS[activeSection.id]}</p>
                    ) : null}
                  </div>
                  <div className="grid grid-cols-1 gap-x-10 lg:grid-cols-2 [&>*]:border-b [&>*]:border-black/[0.04] dark:[&>*]:border-white/[0.06]">
                    {activeSection.settings.map((s) => (
                      <LinkRow
                        key={s.key}
                        s={s}
                        value={s.key in values ? values[s.key] : (s.value ?? "")}
                        onChange={(v) => set(s.key, v)}
                        error={errorFor(s)}
                      />
                    ))}
                  </div>
                </GridCell>
              ) : null}
            </>
          )}
        </GridBlock>

        <PlanChangesBar
          ariaLabel="Unsaved link changes"
          lines={changes.lines}
          invalidCount={changes.invalid.length + Object.keys(serverInvalid).length}
          saving={update.isPending}
          saved={saved && changes.lines.length === 0}
          error={update.isError ? update.error.message : null}
          ignored={[]}
          onSave={onSave}
          onUndo={(key) => set(key, baseline[key] ?? "")}
          onUndoAll={() => {
            setServerInvalid({});
            setValues(baseline);
          }}
          open={reviewOpen}
          onOpenChange={setReviewOpen}
          details={{}}
          pending={false}
        />
      </div>
    </TooltipProvider>
  );
}
